// Redux slice for the global Audit log screen — server-side search + pagination.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { IAuditEntry } from "../models/interfaces";
import { getAuditLogs } from "../serviceHelper";

interface AuditState {
	entries: IAuditEntry[];
	total: number;
	page: number;
	pageSize: number;
	search: string;
	// Include the raw rows auto-logged by the backend middleware (every HTTP
	// mutation, including failed and unauthorized attempts). Off by default —
	// the tab then shows only the curated semantic events.
	includeSystem: boolean;
	isLoading: boolean;
	error: string | null;
}

export const initialState: AuditState = {
	entries: [],
	total: 0,
	page: 1,
	pageSize: 20,
	search: "",
	includeSystem: false,
	isLoading: false,
	error: null,
};

// Fetch a page of the audit log from the live backend. Reads the current
// page/pageSize/search from state so search/pager stay the single source of
// truth. On failure the thunk rejects and the screen surfaces an error.
export const fetchAuditLogs = createAsyncThunk("audit/fetchAuditLogs", async (_: void, { getState }) => {
	const { auditSlice } = getState() as { auditSlice: AuditState };
	const pageData = await getAuditLogs({
		search: auditSlice.search,
		page: auditSlice.page,
		page_size: auditSlice.pageSize,
		include_system: auditSlice.includeSystem,
	});
	return { entries: pageData.items, total: pageData.total };
});

const auditSlice = createSlice({
	name: "auditSlice",
	initialState,
	reducers: {
		setAuditSearch: (state, action: PayloadAction<string>) => {
			state.search = action.payload;
			state.page = 1; // new query starts on page 1
		},
		setAuditPage: (state, action: PayloadAction<{ page: number; pageSize: number }>) => {
			state.page = action.payload.page;
			state.pageSize = action.payload.pageSize;
		},
		setAuditIncludeSystem: (state, action: PayloadAction<boolean>) => {
			state.includeSystem = action.payload;
			state.page = 1; // different result set starts on page 1
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAuditLogs.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchAuditLogs.fulfilled, (state, action) => {
				state.isLoading = false;
				state.error = null;
				state.entries = action.payload.entries;
				state.total = action.payload.total;
			})
			.addCase(fetchAuditLogs.rejected, (state, action) => {
				state.isLoading = false;
				state.entries = [];
				state.total = 0;
				state.error = action.error.message ?? "Failed to load audit log";
			});
	},
});

export const { setAuditIncludeSystem, setAuditPage, setAuditSearch } = auditSlice.actions;
export default auditSlice.reducer;
